// src/components/graphs/SystemDefectFrequency.js

import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    Title,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import axios from 'axios';
import { BASE_URL } from 'BaseURL';
import { COLORS } from 'constants/colors';
import useWindowSize from 'hooks/useWindowResize';
import Spinner from 'components/Spinner';
import './ChartStyles.css';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const SystemDefectFrequency = ({ filter }) => {
    const { width } = useWindowSize();
    const isMobile = width < 768;

    const [chartData, setChartData] = useState(null);
    const { token } = useContext(AuthContext);
    const [error, setError] = useState(null);

    // Reference to the chart instance
    const chartRef = useRef(null);

    useEffect(() => {
        const fetchSystemDefectFrequency = async () => {
            try {
                const params = {
                    ...(filter.vehicleModel && { vehicle_model: filter.vehicleModel }),
                    ...(filter.startDate && { start_date: filter.startDate }),
                    ...(filter.endDate && { end_date: filter.endDate }),
                };

                const response = await axios.get(
                    `${BASE_URL}shiftlens_warehouse/api/system-defect-frequency/`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params,
                    }
                );

                let data = response.data;

                if (data.length > 10) {
                    data = groupSmallCategories(data);
                }

                // Filter out data points with zero values
                data = data.filter((item) => item.complaint_count !== 0);

                const labels = data.map((item) => item.system_name);
                const counts = data.map((item) => item.complaint_count);

                setChartData({
                    labels,
                    datasets: [
                        {
                            data: counts,
                            backgroundColor: labels.map(
                                (_, index) => COLORS[index % COLORS.length]
                            ),
                        },
                    ],
                });
            } catch {
                setError('Failed to load data.');
            }
        };

        fetchSystemDefectFrequency();
    }, [token, filter]);

    // Hide 'unknown' category by default
    useEffect(() => {
        if (chartRef.current && chartData) {
            const chart = chartRef.current;

            // Find the index of 'unknown' label
            const unknownIndex = chartData.labels.findIndex(
                (label) => label.toLowerCase() === 'unknown'
            );

            if (unknownIndex !== -1) {
                // Initially hide 'unknown' data point
                chart.toggleDataVisibility(unknownIndex);
                chart.update();
            }
        }
    }, [chartData]);

    const groupSmallCategories = (data) => {
        const sortedData = data.sort(
            (a, b) => b.complaint_count - a.complaint_count
        );
        const topCategories = sortedData.slice(0, 9);
        const others = sortedData.slice(9);
        const otherCount = others.reduce(
            (sum, item) => sum + item.complaint_count,
            0
        );

        return [
            ...topCategories,
            { system_name: 'Others', complaint_count: otherCount },
        ];
    };

    const options = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const value = context.raw;
                            if (value === 0) {
                                return ''; // Hide tooltip for zero values
                            }
                            const total = context.dataset.data.reduce(
                                (acc, val) => acc + val,
                                0
                            );
                            const percentage = ((value / total) * 100).toFixed(1);
                            return `${context.label}: ${value} (${percentage}%)`;
                        },
                    },
                },
                legend: {
                    position: 'right',
                    labels: {
                        font: {
                            size: isMobile ? 10 : 12,
                        },
                    },
                    onClick: (e, legendItem, legend) => {
                        const index = legendItem.index;
                        const ci = legend.chart;

                        ci.toggleDataVisibility(index);
                        ci.update();
                    },
                },
                datalabels: {
                    display: function (context) {
                        const value = context.dataset.data[context.dataIndex];
                        return value !== 0;
                    },
                    color: 'black',
                    align: 'center',
                    anchor: 'center',
                    font: {
                        weight: 'bold',
                        size: 12,
                    },
                    formatter: (value, context) => {
                        const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${percentage}%`;
                    },
                },
            },
        }),
        [isMobile]
    );
    return (
        <div className="chart-wrapper chart-height">
            {error && <p className="error-message">{error}</p>}
            {chartData ? (
                <Doughnut
                    data={chartData}
                    options={options}
                    ref={(chart) => {
                        chartRef.current = chart;
                    }}
                />
            ) : (
                <Spinner />
            )}
        </div>
    );
};

export default React.memo(SystemDefectFrequency);
